import React, { useEffect, useMemo, useState, useRef } from "react";
import http from "../../api/http";
import "./productGrid.css";
import { FiSearch, FiX, FiPlus, FiMinus, FiShoppingCart } from "react-icons/fi";
import { useBrand } from "../../context/brand-context";
import ProductsFilter from "../ProductFilter/ProductsFilter";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Pager from "../ui/Pager";
import { useAuth } from "../../context/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, increaseQty, decreaseQty, removeFromCart } from "../../context/MyCartSlice";
import { toast } from "react-toastify";

// ─────────────────────────────────────────────────────────────
// Carte produit
// Gère deux modes :
//   • mode normal     : item est un article TabStock complet
//   • mode VIN en stock  (vinOutOfStock=false) : item = { rxpRef, rxpName, rxpBrand, inStock:true, stock:{...} }
//   • mode VIN hors stock (vinOutOfStock=true) : item = { rxpRef, rxpName, rxpBrand, inStock:false, stock:null }
// ─────────────────────────────────────────────────────────────
function ProductCard({ item, vinOutOfStock = false }) {
  const { brand } = useBrand();
  const isAmen = brand === "amen";
  const brandColor = isAmen ? "#7a0f18" : "#2563eb";
  const priceColor  = isAmen ? "#111827"  : brandColor; // prix en noir pour Amen
  const dispatch = useDispatch();
  const [imgError, setImgError] = useState(false);
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  // Clé panier = même CodArt que celui stocké par le reducer (normalizeItem)
  const cartCod = item?.CodArt ?? item?.RefArt ?? item?.rxpRef ?? "NA";
  // Quantité de CET article dans le panier = source unique de vérité
  const inCart = useSelector((s) => {
    const f = s.cart.items.find((x) => String(x.CodArt) === String(cartCod));
    return f ? Number(f.qty) : 0;
  });

  // ── Résolution des champs selon le mode ──
  const isVinMode = item?.rxpRef !== undefined; // présence de rxpRef = provient de l'API by-vehicle

  // VIN API returns flat items (no nested `stock`) — use item directly
  const stockData = isVinMode ? item : null;

  const title       = isVinMode
    ? (stockData?.LibArt || item?.rxpName || "Article")
    : (item?.LibArt || "Article");

  const brandLabel  = isVinMode
    ? (stockData?.Marque || item?.rxpBrand || "")
    : (item?.Marque || item?.RepXpert?.Brand || "");

  const refDisplay  = isVinMode
    ? (item?.rxpRef || "")
    : (item?.RefArt || item?.CodArt || "");

  const price       = isVinMode
    ? (typeof stockData?.PrixTTC === "number" ? stockData.PrixTTC : null)
    : (typeof item?.PrixTTC === "number" ? item.PrixTTC : null);

  const qt          = isVinMode
    ? Number(stockData?.Qt || 0)
    : Number(item?.Qt || 0);

  const imgMarque   = isVinMode ? (stockData?.ImgMarque || null) : (item?.ImgMarque || null);

  const status      = vinOutOfStock ? "Non disponible" : (qt > 0 ? "Disponible" : "Rupture");
  const statusColor = vinOutOfStock ? "#9ca3af"        : (qt > 0 ? "#16a34a"    : "#ef4444");

  const repXpert = !isVinMode && item?.RepXpert?.enrichi ? item.RepXpert : null;

  const img = useMemo(() => {
    // Catalogue : image article = TabStock.imgArt servie par l'API (ImgArt), pas RepXpert
    if (item?.ImgArt) return item.ImgArt;
    if (isVinMode) return item?.RepXpert?.Images?.[0] || null;
    return null;
  }, [item, isVinMode]);

  // lien vers détail : utilise CodArt du stock si disponible, sinon rxpRef
  const refOrCod = isVinMode
    ? (stockData?.CodArt || item?.rxpRef || "non-disponible")
    : (item?.RefArt || "non-disponible");

  // ── Carte hors stock (mode VIN) ──
  if (vinOutOfStock) {
    return (
      <article className="p-card p-card--oos">
        <div className="p-imgWrap p-imgWrap--vin">
          <div className="p-imgPh" />
        </div>
        <div className="p-body">
          <div className="p-status">
            <span className="p-dot" style={{ background: statusColor }} />
            <span className="p-statusText">{status}</span>
          </div>
          <h3 className="p-title">{title}</h3>
          {brandLabel && <div className="p-ref">Marque : {brandLabel}</div>}
          {refDisplay  && <div className="p-ref">Réf : {refDisplay}</div>}
        </div>
      </article>
    );
  }

  // ── Carte normale ou en-stock VIN ──
  return (
    <article className="p-card p-card--clickable">
      <Link
        className="p-card__overlay"
        to={`/produits/${encodeURIComponent(refOrCod)}`}
        state={{ item: item }}
        aria-label={`Voir les détails de ${title}`}
      />
      <div className={`p-imgWrap${isVinMode ? " p-imgWrap--vin" : ""}`}>
        {imgMarque && (
          <div className="p-brandLogo">
            <img src={imgMarque} alt="Marque" />
          </div>
        )}
        {img && !imgError ? (
          <img src={img} alt={title} loading="lazy" className="p-mainImg" onError={() => setImgError(true)} />
        ) : (
          <div className="p-imgPh" />
        )}
      </div>
      <div className="p-body">
        <div className="p-status">
          <span className="p-dot" style={{ background: statusColor }} />
          <span className="p-statusText">{status}</span>
        </div>
        <h3 className="p-title">{title}</h3>

        {/* Infos mode normal */}
        {!isVinMode && repXpert?.Designation && repXpert.Designation !== title && (
          <div className="p-designation">{repXpert.Designation}</div>
        )}
        
        {!isVinMode && <div className="p-ref">Réf Originale: {item?.CodArtOrig}</div>}
        {!isVinMode && <div className="p-ref">Réf : {item?.CodArt}</div>}
        {!isVinMode && repXpert?.ArtNumber && (
          <div className="p-ref">Réf RepXpert: {repXpert.ArtNumber}</div>
        )}

        {/* Infos mode VIN en stock */}
        {isVinMode && brandLabel && <div className="p-ref">Marque : {brandLabel}</div>}
        {isVinMode && stockData?.CodArt && <div className="p-ref">Réf : {stockData.CodArt}</div>}
        {isVinMode && item?.rxpRef && <div className="p-ref">Réf RepXpert : {item.rxpRef}</div>}

        <div className="p-foot">
          {qt > 0 && (
            <>
              <div className="p-price" style={{ color: priceColor }}>
                {price != null ? `${price.toFixed(2)} DT` : "—"}
              </div>
              <div className="p-actions" onClick={stop}>
                {inCart > 0 ? (
                  // Déjà au panier → stepper qui pilote DIRECTEMENT la ligne du panier
                  <div className="p-qty p-qty--in" role="group" aria-label="Quantité au panier">
                    <button
                      type="button" className="p-qtyBtn" aria-label={inCart <= 1 ? "Retirer du panier" : "Diminuer"}
                      onClick={(e) => {
                        stop(e);
                        if (inCart <= 1) { dispatch(removeFromCart(cartCod)); toast.info("Retiré du panier"); }
                        else dispatch(decreaseQty(cartCod));
                      }}
                    >
                      <FiMinus />
                    </button>
                    <span className="p-qtyVal" aria-live="polite">{inCart}</span>
                    <button
                      type="button" className="p-qtyBtn" aria-label="Augmenter"
                      onClick={(e) => { stop(e); dispatch(increaseQty(cartCod)); }}
                    >
                      <FiPlus />
                    </button>
                  </div>
                ) : (
                  // Pas encore au panier → bouton Ajouter seul
                  <button
                    type="button"
                    className="p-buy p-buy--enter"
                    style={{ borderColor: brandColor, color: "#fff", background: brandColor }}
                    onClick={(e) => {
                      stop(e);
                      dispatch(addToCart({ item, qty: 1, dbName: isAmen ? "Amen" : "Amen_C" }));
                      toast.success(`${String(title).slice(0, 32)} ajouté au panier`);
                    }}
                  >
                    <FiShoppingCart style={{ marginRight: 6 }} />
                    Ajouter
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────
// Bannière véhicule — adapté à la réponse de /api/articles/by-vehicle
// Réponse : { externalId, vehicleUuid, count, inStockCount, items, pagination }
// ─────────────────────────────────────────────────────────────
function VinBanner({ vin, externalId, vehicleUuid, count, inStockCount, vinFilter, onChangeVinFilter, onClear, brandColor, accentColor, vehicleCandidates }) {
  return (
    <div className="vin-banner">
      <div className="vin-banner__info">
        <span className="vin-banner__car">🚗</span>
        <div>
          {vin ? (
            <div className="vin-banner__name">
              VIN : {vin}
              {vehicleCandidates?.length > 1 && (
                <span
                  className="vin-banner__candidates"
                  title="Plusieurs variantes trouvées pour ce VIN — premier résultat sélectionné automatiquement"
                >
                  ⚠️ {vehicleCandidates.length} variantes
                </span>
              )}
            </div>
          ) : (
            <div className="vin-banner__name">Véhicule #{externalId}</div>
          )}
          {vehicleUuid && (
            <div className="vin-banner__vin">UUID : {vehicleUuid}</div>
          )}
        </div>
      </div>

      <div className="vin-banner__filters">
        <button
          className={`vin-tab${vinFilter === "all" ? " vin-tab--active" : ""}`}
          style={vinFilter === "all" ? { borderColor: accentColor, color: accentColor } : {}}
          onClick={() => onChangeVinFilter("all")}
        >
          Toutes ({count ?? 0})
        </button>
        <button
          className={`vin-tab${vinFilter === "in" ? " vin-tab--active" : ""}`}
          style={vinFilter === "in" ? { borderColor: accentColor, color: accentColor } : {}}
          onClick={() => onChangeVinFilter("in")}
        >
          En stock ({inStockCount ?? 0})
        </button>
      </div>

      <button className="vin-banner__reset" onClick={onClear} title="Réinitialiser la recherche">
        <FiX size={14} /> Réinitialiser
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Grille principale
// ─────────────────────────────────────────────────────────────
export default function ProductsHeroAndGrid({ onError, vinResult, onClearVin }) {
  const { brand } = useBrand();
  const isAmen = brand === "amen";
  const brandColor    = isAmen ? "#7a0f18" : "#2563eb";
  const accentColor   = isAmen ? "#7a0f18" : brandColor; // orange logo → onglets/indicateurs
  const searchBorder  = isAmen ? "#9ca3af" : brandColor; // gris neutre → barre de recherche
  const dbName = isAmen ? "Amen" : "Amen_C";

  // Recherche / filtres / page conservés pendant la session : retour depuis la fiche produit
  // = on retrouve la grille telle qu'on l'a laissée (le composant est démonté entre-temps)
  const stateKey = `productGrid:${dbName}`;
  const [saved] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(stateKey)) || {}; } catch { return {}; }
  });

  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState(saved.q || "");
  const [stockFilter, setStockFilter] = useState(saved.stockFilter || "all");
  const [selectedBrands, setSelectedBrands] = useState(saved.selectedBrands || []);
  const [pageIndex, setPageIndex] = useState(1);
  const restorePageRef = useRef(saved.pageIndex > 1 ? saved.pageIndex : null);
  const [pageSize, setPageSize] = useState(12);
  const [vinFilter, setVinFilter] = useState("all"); // "all" | "in"

  const gridZoneRef = useRef(null);
  const searchWrapRef = useRef(null);
  const lastSavedRef = useRef({ q: "", ts: 0 });
  const { user } = useAuth();

  // Reset filtre VIN quand le résultat change
  useEffect(() => { setVinFilter("all"); setPageIndex(1); }, [vinResult]);

  // Calcul colonnes pour pageSize dynamique
  useEffect(() => {
    const updatePageSize = () => {
      if (gridZoneRef.current) {
        const grid = gridZoneRef.current.querySelector(".p-grid");
        if (grid) {
          const cols = window.getComputedStyle(grid)
            .getPropertyValue("grid-template-columns").split(" ").length;
          setPageSize(Math.max(12, cols * 4));
        }
      }
    };
    updatePageSize();
    window.addEventListener("resize", updatePageSize);
    return () => window.removeEventListener("resize", updatePageSize);
  }, [items, q, selectedBrands, vinResult]);

  // Fetch articles normaux (désactivé en mode véhicule)
  useEffect(() => {
    if (vinResult) return;
    let alive = true;
    setItems(null);
    setErr("");
    (async () => {
      try {
        const codtiers = user?.codtiers || null;
        const { data } = await http.post(
          "/api/articles",
          { codtiers },
          // catalogue complet (~12k lignes) : SQL à froid peut dépasser 15s → timeout dédié
          { headers: { "x-db": dbName }, timeout: 60000 }
        );
        const rows = Array.isArray(data?.items) ? data.items : [];
        if (alive) setItems(rows);
      } catch (e) {
        if (alive) {
          setErr(e?.message || "Erreur réseau");
          setItems([]);
          onError?.(e);
        }
      }
    })();
    return () => { alive = false; };
  }, [dbName, user?.codtiers, vinResult]);

  // ── Mode véhicule : filtrage local "En stock" ──
  // La réponse API : { items: [...], count, inStockCount, externalId, vehicleUuid }
  const vinParts = useMemo(() => {
    if (!vinResult?.items) return null;
    if (vinFilter === "in") return vinResult.items.filter((p) => p.inStock);
    return vinResult.items;
  }, [vinResult, vinFilter]);

  // ── Mode normal : filtrage texte + marque + stock ──
  const norm    = (s) => String(s || "").toLowerCase().trim();
  const normRef = (s) => String(s || "").toLowerCase().trim().replace(/[\s\-_.\/]+/g, "");

  const brandCounts = useMemo(() => {
    const counts = {};
    (items || []).forEach((it) => {
      const key = (it?.Marque || "").trim();
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [items]);

  // Un article correspond si CHAQUE mot tapé se retrouve quelque part
  // (libellé, désignation, marque OU référence/référence d'origine) —
  // ainsi "partie du libellé" + "partie de la référence" dans une même
  // recherche fait quand même remonter l'article.
  const matchesToken = (it, kText, kRef) =>
    norm(it?.LibArt).includes(kText) ||
    norm(it?.RepXpert?.Designation).includes(kText) ||
    norm(it?.RepXpert?.Brand).includes(kText) ||
    (it?.TecDoc?.InfoMain?.articles || []).some((a) =>
      norm(a?.articleProductName).includes(kText)
    ) ||
    (kRef.length > 0 &&
      (normRef(it?.CodArt).includes(kRef) ||
        normRef(it?.CodArtOrig).includes(kRef) ||
        normRef(it?.RefArt).includes(kRef) ||
        normRef(it?.RepXpert?.ArtNumber).includes(kRef) ||
        (it?.RepXpert?.OEM || []).some((eq) => normRef(eq).includes(kRef)) ||
        normRef(it?.TecDoc?.ArtNumber).includes(kRef)));

  const filtered = useMemo(() => {
    if (!items) return null;
    const tokens = norm(q).split(/\s+/).filter(Boolean);
    const selNorm = selectedBrands.map((b) => norm(b));
    return items.filter((it) => {
      const matchesSearch =
        tokens.length === 0 ||
        tokens.every((t) => matchesToken(it, t, normRef(t)));
      if (!matchesSearch) return false;
      const qt = Number(it?.Qt || 0);
      if (stockFilter === "in"  && qt <= 0) return false;
      if (stockFilter === "out" && qt > 0)  return false;
      if (selNorm.length > 0 && !selNorm.includes(norm(it?.Marque))) return false;
      return true;
    });
  }, [items, q, stockFilter, selectedBrands]);

  useEffect(() => {
    // 1er chargement des articles après un retour : on remet la page mémorisée au lieu de 1
    if (restorePageRef.current && items?.length) {
      setPageIndex(restorePageRef.current);
      restorePageRef.current = null;
      return;
    }
    setPageIndex(1);
  }, [q, stockFilter, selectedBrands, items]);

  useEffect(() => {
    try {
      sessionStorage.setItem(stateKey, JSON.stringify({ q, stockFilter, selectedBrands, pageIndex }));
    } catch { /* stockage indisponible : on garde juste l'état en mémoire */ }
  }, [stateKey, q, stockFilter, selectedBrands, pageIndex]);

  // ── Pagination (source = mode véhicule ou mode normal) ──
  const sourceList = vinResult ? vinParts : filtered;

  const totalPages = useMemo(() => {
    if (!sourceList) return 1;
    return Math.max(1, Math.ceil(sourceList.length / pageSize));
  }, [sourceList, pageSize]);

  // Page mémorisée devenue hors limites (catalogue / largeur d'écran changés) → dernière page
  useEffect(() => {
    if (pageIndex > totalPages) setPageIndex(totalPages);
  }, [pageIndex, totalPages]);

  const displayed = useMemo(() => {
    if (!sourceList) return null;
    const start = (pageIndex - 1) * pageSize;
    return sourceList.slice(start, start + pageSize);
  }, [sourceList, pageIndex, pageSize]);

  // ── Log recherche texte ──
  const logSearch = async () => {
    const text = q.trim();
    if (text.length < 2) return;
    const now = Date.now();
    if (lastSavedRef.current.q === text && now - lastSavedRef.current.ts < 3000) return;
    lastSavedRef.current = { q: text, ts: now };
    try {
      await http.post(
        "/api/search-history",
        { q: text, stockFilter, selectedBrands, page: "/produits" },
        { headers: { "x-db": dbName } }
      );
    } catch (e) { console.warn(e); }
  };

  // ════════════════════════════════════════
  // RENDU — MODE VÉHICULE
  // ════════════════════════════════════════
  if (vinResult) {
    return (
      <div id="products-section">
        <VinBanner
          vin={vinResult.vin}
          externalId={vinResult.externalId}
          vehicleUuid={vinResult.vehicleUuid}
          count={vinResult.count}
          inStockCount={vinResult.inStockCount}
          vehicleCandidates={vinResult.vehicleCandidates}
          vinFilter={vinFilter}
          onChangeVinFilter={(f) => { setVinFilter(f); setPageIndex(1); }}
          onClear={onClearVin}
          brandColor={brandColor}
          accentColor={accentColor}
        />

        <div className="p-vin-layout" ref={gridZoneRef}>
          {!vinParts && (
            <div className="p-grid">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="p-card p-skel" />
              ))}
            </div>
          )}
          {vinParts && (
            <div className="p-grid">
              {vinParts.map((part, i) => (
                <ProductCard
                  key={part.stock?.CodArt || part.rxpRef || i}
                  item={part}
                  vinOutOfStock={!part.inStock}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════
  // RENDU — MODE NORMAL
  // ════════════════════════════════════════
  return (
    <div id="products-section">
      <section className="hero-wrap">
        <div className="hero-left">
          <h1 className="hero-title">Achetez toutes les pièces automobiles</h1>
          <p className="hero-sub">Trouvez rapidement des pièces fiables pour votre véhicule. Prix compétitifs.</p>
        </div>
        <div ref={searchWrapRef}>
          <form className="hero-search" onSubmit={(e) => e.preventDefault()}>
            <input
              className="hero-input"
              type="text"
              placeholder="Recherche ..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ borderColor: searchBorder }}
            />
            {q && (
              <button
                type="button"
                className="hero-clear"
                aria-label="Effacer la recherche"
                onClick={() => setQ("")}
              >
                <FiX />
              </button>
            )}
            <button
              className="hero-btn"
              style={{ borderColor: searchBorder, color: searchBorder }}
              onClick={logSearch}
            >
              <FiSearch />
            </button>
          </form>
        </div>
      </section>

      <div className="p-gridLayout">
        <ProductsFilter
          stock={stockFilter}
          onChangeStock={setStockFilter}
          selectedBrands={selectedBrands}
          onToggleBrand={(name) =>
            setSelectedBrands((prev) =>
              prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
            )
          }
          onClear={() => { setSelectedBrands([]); setStockFilter("all"); setQ(""); }}
          brandCounts={brandCounts}
        />

        <div className="p-gridZone" ref={gridZoneRef}>
          {err && (
            <div className="p-error">
              <span>⚠️ {typeof err === "string" ? err : "Une erreur est survenue."}</span>
              <button type="button" className="p-error__btn" onClick={() => window.location.reload()}>Réessayer</button>
            </div>
          )}

          {!displayed && !err && (
            <>
              <div className="top-progress" />
              <div className="p-grid">
                {Array.from({ length: pageSize || 12 }).map((_, i) => (
                  <div key={i} className="p-card p-skel" />
                ))}
              </div>
            </>
          )}

          {displayed && (
            <>
              <div className="p-resultBar" aria-live="polite">
                <span className="p-count">
                  {(filtered?.length ?? 0).toLocaleString("fr-FR")} résultat{(filtered?.length || 0) > 1 ? "s" : ""}
                </span>
              </div>

              {displayed.length === 0 ? (
                <div className="p-empty">
                  <div className="p-empty__icon">🔍</div>
                  <h3 className="p-empty__title">Aucun résultat</h3>
                  <p className="p-empty__text">Essayez une autre référence, marque, ou réinitialisez les filtres.</p>
                  <button
                    type="button"
                    className="p-empty__btn"
                    onClick={() => { setSelectedBrands([]); setStockFilter("all"); setQ(""); }}
                  >
                    Réinitialiser les filtres
                  </button>
                </div>
              ) : (
                <>
                  <div className="p-grid">
                    {displayed.map((it) => (
                      <ProductCard key={it.CodArt || it.RefArt} item={it} />
                    ))}
                  </div>
                  <Pager totalPages={totalPages} page={pageIndex} onChange={setPageIndex} />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
